import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ManualLoader, Wrapper } from "../../components";
import { WizardShell } from "../../components/Layouts/WizardShell";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetIncomeLimitsQuery,
  useGetUnitDataMutation,
} from "../../store/services/publicApi";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { theme } from "../../shared/theme/theme";
import { COLORS } from "../../shared/theme/colorConfig";
import {
  RegisterReviewContainer,
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../Account/Register/Register.style";
import { getAffordableUnitAssignmentMode } from "../Account/Register/unitHelpers";
import { MINIMUM_INCOME_RENT_MULTIPLIER } from "../Account/Register/incomeRequirement";

interface PropertyInfo {
  id: string;
  name: string;
  abbreviation?: string;
  affordableUnitAssignmentMode?: "holding" | "direct";
  streetAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
}

interface IncomeLimitsResponse {
  property: PropertyInfo;
  incomeLimits: Record<number, number>; // householdSize → incomeLimit
}

type EligibilityResult = "eligible" | "overIncome" | "underMinimum" | null;

const parseIncome = (raw: string): number | null => {
  const cleaned = raw.replace(/[,$\s]/g, "");
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const parsePositiveMoney = (raw: unknown): number | null => {
  if (typeof raw === "number") {
    return Number.isFinite(raw) && raw > 0 ? raw : null;
  }
  if (typeof raw !== "string") return null;

  const cleaned = raw.replace(/[,$\s]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const AffordablePrequalifyScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const propertyId =
    searchParams.get("propertyId") || searchParams.get("propertyID") || "";
  const selectedUnitId =
    searchParams.get("unitID") || searchParams.get("unitNumber") || "";
  const selectedMoveInDate = searchParams.get("moveInDate") || "";
  const selectedLeaseTerm =
    searchParams.get("leaseTerm") || searchParams.get("term") || "";
  const hasSelectedUnitContext = Boolean(
    selectedUnitId && selectedMoveInDate && selectedLeaseTerm
  );

  const { data, isLoading, error } = useGetIncomeLimitsQuery(propertyId, {
    skip: !propertyId,
  }) as {
    data: IncomeLimitsResponse | undefined;
    isLoading: boolean;
    error: any;
  };

  const [step, setStep] = useState<1 | 2>(1);
  const [householdSize, setHouseholdSize] = useState<number | "">("");
  const [incomeInput, setIncomeInput] = useState<string>("");
  const [eligibilityResult, setEligibilityResult] =
    useState<EligibilityResult>(null);
  const [selectedUnitMonthlyRent, setSelectedUnitMonthlyRent] = useState<
    number | null
  >(null);
  const [selectedUnitRentLoadFailed, setSelectedUnitRentLoadFailed] =
    useState(false);
  const [getUnitDataMutation, { isLoading: isLoadingSelectedUnit }] =
    useGetUnitDataMutation();

  const incomeLimit =
    householdSize && data?.incomeLimits
      ? data.incomeLimits[householdSize] ?? null
      : null;

  const parsedIncome = parseIncome(incomeInput);
  const minimumAnnualIncome =
    selectedUnitMonthlyRent !== null
      ? selectedUnitMonthlyRent * MINIMUM_INCOME_RENT_MULTIPLIER * 12
      : null;
  const canCheck =
    parsedIncome !== null &&
    incomeLimit !== null &&
    (!hasSelectedUnitContext || minimumAnnualIncome !== null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleCheckEligibility = () => {
    if (!canCheck || parsedIncome === null || incomeLimit === null) return;
    if (minimumAnnualIncome !== null && parsedIncome < minimumAnnualIncome) {
      setEligibilityResult("underMinimum");
      return;
    }
    setEligibilityResult(
      parsedIncome <= incomeLimit ? "eligible" : "overIncome"
    );
  };

  const handleContinueToIncome = () => {
    if (!householdSize) return;
    setStep(2);
  };

  const handleChangeHousehold = () => {
    setStep(1);
    setIncomeInput("");
    setEligibilityResult(null);
  };

  const handleReset = () => {
    setStep(1);
    setHouseholdSize("");
    setIncomeInput("");
    setEligibilityResult(null);
  };

  const householdSizeLabel =
    householdSize === 1 ? "One Person" : `${householdSize} People`;

  useEffect(() => {
    if (!hasSelectedUnitContext || !propertyId) {
      setSelectedUnitMonthlyRent(null);
      setSelectedUnitRentLoadFailed(false);
      return;
    }

    let isActive = true;
    setSelectedUnitMonthlyRent(null);
    setSelectedUnitRentLoadFailed(false);
    setEligibilityResult(null);

    getUnitDataMutation({
      unitID: selectedUnitId,
      moveInDate: selectedMoveInDate,
      term: selectedLeaseTerm,
      propertyID: propertyId,
      leaseId: null,
    })
      .then((res: any) => {
        if (!isActive) return;

        const rent = parsePositiveMoney(res?.data?.rent);
        if (res?.error || rent === null) {
          setSelectedUnitMonthlyRent(null);
          setSelectedUnitRentLoadFailed(true);
          return;
        }

        setSelectedUnitMonthlyRent(rent);
        setSelectedUnitRentLoadFailed(false);
      })
      .catch(() => {
        if (!isActive) return;
        setSelectedUnitMonthlyRent(null);
        setSelectedUnitRentLoadFailed(true);
      });

    return () => {
      isActive = false;
    };
  }, [
    getUnitDataMutation,
    hasSelectedUnitContext,
    propertyId,
    selectedLeaseTerm,
    selectedMoveInDate,
    selectedUnitId,
  ]);

  // Error states
  if (!propertyId) {
    return (
      <Wrapper>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h4">
            {t("affordable.prequalify.error.propertyNotFound")}
          </Typography>
        </Box>
      </Wrapper>
    );
  }

  if (isLoading) return <ManualLoader />;

  if (error) {
    const errorMessage =
      error?.data?.message || t("affordable.prequalify.error.propertyNotFound");
    return (
      <Wrapper>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h4">{errorMessage}</Typography>
        </Box>
      </Wrapper>
    );
  }

  if (data && Object.keys(data.incomeLimits).length === 0) {
    return (
      <Wrapper>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h4">
            {t("affordable.prequalify.error.noIncomeLimits")}
          </Typography>
        </Box>
      </Wrapper>
    );
  }

  return (
    <WizardShell>
      <RegisterReviewContainer>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {data?.property?.name}
          {data?.property?.city ? `, ${data.property.city}` : ""}
        </Typography>
        <RegisterReviewSection>
          <RegisterReviewSectionTitle>
            {t("affordable.prequalify.title")}
          </RegisterReviewSectionTitle>

          {/* Info Banner */}
          <Card
            variant="outlined"
            sx={{
              mb: 2,
              backgroundColor: COLORS.gray[50],
              borderColor: COLORS.gray[200],
            }}
          >
            <CardContent sx={{ display: "flex", gap: 1 }}>
              <InfoOutlinedIcon sx={{ color: COLORS.gray[600], mt: 0.25 }} />
              <Box>
                <Typography variant="h3Bold">
                  {t("affordable.prequalify.incomeRestricted")}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {t("affordable.prequalify.incomeRestrictedDescription")}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Step 1: Household Size */}
          {step === 1 && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t("affordable.prequalify.householdSizeLabel")}
                </Typography>
                <Select
                  value={householdSize}
                  onChange={(e) => {
                    setHouseholdSize(e.target.value as number);
                  }}
                  displayEmpty
                  sx={{
                    borderRadius: 1,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gray[300],
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Choose...
                  </MenuItem>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size === 1 ? "One Person" : `${size} People`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={!householdSize}
                onClick={handleContinueToIncome}
                sx={{ py: 1.5 }}
              >
                {t("affordable.prequalify.continueToIncome")}
              </Button>
            </>
          )}

          {/* Step 2: Household Summary + Income Limit + Income Input */}
          {step === 2 && householdSize && incomeLimit !== null && (
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  pb: 2,
                  borderBottom: `1px solid ${COLORS.gray[200]}`,
                }}
              >
                <Typography variant="body1">
                  <strong>{t("affordable.prequalify.householdSize")}:</strong>{" "}
                  {householdSizeLabel}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleChangeHousehold}
                  disabled={eligibilityResult !== null}
                >
                  {t("affordable.prequalify.change")}
                </Button>
              </Box>
              <Typography variant="body1" component="div" sx={{ mb: 1 }}>
                {t("affordable.prequalify.incomeInputLabel")}
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 2.5,
                  mt: 0,
                  mb: 2,
                  color: COLORS.gray[600],
                }}
              >
                <Typography
                  component="li"
                  variant="body2"
                  sx={{ mb: 0.5, fontWeight: 600 }}
                >
                  {t("affordable.prequalify.incomeInputHelperGross")}
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  {t("affordable.prequalify.incomeInputHelperInclude")}
                </Typography>
                <Typography component="li" variant="body2">
                  {t("affordable.prequalify.incomeInputHelperFallback")}
                </Typography>
              </Box>

              <TextField
                fullWidth
                value={incomeInput}
                onChange={(e) => {
                  setIncomeInput(e.target.value);
                  setEligibilityResult(null);
                }}
                placeholder="0"
                inputProps={{
                  inputMode: "decimal",
                  "aria-label": "Annual gross household income",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {hasSelectedUnitContext && minimumAnnualIncome !== null && (
                <Typography
                  variant="body2"
                  sx={{ mb: 2, color: COLORS.gray[600] }}
                >
                  Minimum income for this unit ({MINIMUM_INCOME_RENT_MULTIPLIER}
                  x monthly rent):{" "}
                  <strong>{formatCurrency(minimumAnnualIncome)}</strong>
                </Typography>
              )}

              {hasSelectedUnitContext && minimumAnnualIncome === null && (
                <Alert
                  severity={selectedUnitRentLoadFailed ? "warning" : "info"}
                  sx={{ mb: 2 }}
                >
                  {selectedUnitRentLoadFailed
                    ? t("affordable.prequalify.error.unitRentUnavailable")
                    : t("affordable.prequalify.loadingUnitRent")}
                </Alert>
              )}

              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={!canCheck || isLoadingSelectedUnit}
                onClick={handleCheckEligibility}
                sx={{ py: 1.5 }}
              >
                {t("affordable.prequalify.checkEligibility")}
              </Button>
            </Box>
          )}

          {/* Eligibility Result */}
          {eligibilityResult === "eligible" && incomeLimit !== null && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="h3Bold">
                  {t("affordable.prequalify.eligible.title")}
                </Typography>
                <Typography variant="body2">
                  {t("affordable.prequalify.eligible.message")}
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: `1px solid ${COLORS.gray[200]}`,
                  }}
                >
                  <Typography variant="body2">
                    Income limit for {householdSizeLabel}:{" "}
                    <strong>{formatCurrency(incomeLimit)}</strong>
                  </Typography>
                  {minimumAnnualIncome !== null && (
                    <Typography variant="body2">
                      Minimum income for this unit:{" "}
                      <strong>{formatCurrency(minimumAnnualIncome)}</strong>
                    </Typography>
                  )}
                  {parsedIncome !== null && (
                    <Typography variant="body2">
                      You reported:{" "}
                      <strong>{formatCurrency(parsedIncome)}</strong>
                    </Typography>
                  )}
                </Box>
              </Alert>

              {/* Confirmation before proceeding */}
              <Card variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You are applying for an income restricted apartment unit
                  within our community.
                </Typography>
                <Button
                  variant="containedLarge"
                  color="primary"
                  fullWidth
                  onClick={() => {
                    if (householdSize) {
                      sessionStorage.setItem(
                        "tp_prequalify_household_size",
                        String(householdSize)
                      );
                    }
                    if (
                      getAffordableUnitAssignmentMode(data?.property) ===
                        "direct" &&
                      !hasSelectedUnitContext
                    ) {
                      navigate(
                        `/availability/${encodeURIComponent(
                          data?.property?.abbreviation || propertyId
                        )}`
                      );
                      return;
                    }
                    const params = new URLSearchParams({ propertyId });
                    if (selectedUnitId) params.set("unitID", selectedUnitId);
                    if (selectedMoveInDate) {
                      params.set("moveInDate", selectedMoveInDate);
                    }
                    if (selectedLeaseTerm) {
                      params.set("leaseTerm", selectedLeaseTerm);
                    }
                    navigate(`/application/register?${params.toString()}`);
                  }}
                >
                  {t("affordable.prequalify.eligible.continueButton")}
                </Button>
              </Card>
            </Box>
          )}

          {eligibilityResult === "underMinimum" &&
            incomeLimit !== null &&
            minimumAnnualIncome !== null && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="h3Bold">
                    {t("affordable.prequalify.underMinimum.title")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t("affordable.prequalify.underMinimum.message")}
                  </Typography>
                  <Box
                    sx={{
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: `1px solid ${COLORS.gray[200]}`,
                    }}
                  >
                    <Typography variant="body2">
                      Minimum income for this unit (
                      {MINIMUM_INCOME_RENT_MULTIPLIER}x monthly rent):{" "}
                      <strong>{formatCurrency(minimumAnnualIncome)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Income limit for {householdSizeLabel}:{" "}
                      <strong>{formatCurrency(incomeLimit)}</strong>
                    </Typography>
                    {parsedIncome !== null && (
                      <Typography variant="body2">
                        You reported:{" "}
                        <strong>{formatCurrency(parsedIncome)}</strong>
                      </Typography>
                    )}
                  </Box>
                </Alert>
                <Button variant="outlined" onClick={handleReset} sx={{ mt: 1 }}>
                  Try Again
                </Button>
              </Box>
            )}

          {eligibilityResult === "overIncome" && incomeLimit !== null && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="h3Bold">
                  {t("affordable.prequalify.overIncome.title")}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {t("affordable.prequalify.overIncome.message")}
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: `1px solid ${COLORS.gray[200]}`,
                  }}
                >
                  <Typography variant="body2">
                    Income limit for {householdSizeLabel}:{" "}
                    <strong>{formatCurrency(incomeLimit)}</strong>
                  </Typography>
                  {parsedIncome !== null && (
                    <Typography variant="body2">
                      You reported:{" "}
                      <strong>{formatCurrency(parsedIncome)}</strong>
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  {t("affordable.prequalify.overIncome.contactPrompt")}
                </Typography>
                {(data?.property?.phone || data?.property?.email) && (
                  <Box sx={{ mt: 1.5 }}>
                    {data?.property?.phone && (
                      <Typography variant="body2">
                        Call us:{" "}
                        <a
                          href={`tel:${data.property.phone}`}
                          style={{ color: theme.palette.primary.main }}
                        >
                          {data.property.phone}
                        </a>
                      </Typography>
                    )}
                    {data?.property?.email && (
                      <Typography variant="body2">
                        Email us:{" "}
                        <a
                          href={`mailto:${data.property.email}`}
                          style={{ color: theme.palette.primary.main }}
                        >
                          {data.property.email}
                        </a>
                      </Typography>
                    )}
                  </Box>
                )}
              </Alert>
              <Button variant="outlined" onClick={handleReset} sx={{ mt: 1 }}>
                Try Again
              </Button>
            </Box>
          )}
        </RegisterReviewSection>
      </RegisterReviewContainer>
    </WizardShell>
  );
};
